import React, { ChangeEvent, Fragment, useState } from 'react'
import { isFreeResponse, isFreeResponseAnswer, Image } from '../../utils'
import { StudentQuestionProps } from '../Question'
import {
	FreeResponse,
	ClientFields,
	StudentAnswer,
	FreeResponseAnswer,
} from '@mission.io/question-toolkit'
import Footer from '../../components/studentPromptFooter'
import { LabelInformation } from '../../assets/styles'
import StudentReviewWrapper from '../../components/student-review'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { QuestionVariant, StudentResponse } from '../../model/general'
import FooterStickyContainer from '../../components/footer-sticky-container/FooterStickyContainer'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

/**
 * A component for a student view where the student can answer the free response question. It also
 * has a reviewed view where the student can see the correct answer of the free question
 * @returns {JSX.Element}
 */
export const StudentFreeResponse = ({
	question,
	requestId,
	review,
	textToSpeech: TextToSpeech,
	sendMessage,
	questionIndex,
	studentResponse,
	parentRef,
}: StudentQuestionProps & {
	studentResponse?: StudentResponse<FreeResponseAnswer> | null
}): JSX.Element => {
	if (!question || !isFreeResponse(question)) return <Fragment />
	const [answer, setAnswer] = useState<string>('')
	const [error, setError] = useState<string>('')

	const handleSubmit = () => {
		if (!answer) {
			setError('Please enter an answer')
			return
		}
		setError('')
		sendMessage &&
			sendMessage({
				type: QuestionVariant.FREE_RESPONSE,
				value: answer,
			})
	}
	const instantReview = review === 'INSTANT'

	return instantReview || (!review && Boolean(requestId)) ? (
		<FooterStickyContainer
			instantReview={instantReview}
			footerContent={
				!instantReview && (
					<Footer
						error={error}
						onClickSubmit={handleSubmit}
						question={question}
						isReview={instantReview}
					/>
				)
			}
			topContent={
				<div>
					<LabelInformation>
						<OptionalMarkdown
							className="prose-on-game-light-purple"
							textToSpeech={TextToSpeech}>
							{question.phrase}
						</OptionalMarkdown>
					</LabelInformation>

					<Image media={question.media} expandable parentRef={parentRef} />
					<br />

					{instantReview ? (
						<div css="background-color: white; padding: 8px; border-radius: 4px; color: black; width: fit-content; margin: 0 auto;">
							{studentResponse?.answer?.value || <i>No answer</i>}
						</div>
					) : (
						<FRQuestionInput
							aria-label="Free response input"
							placeholder="Write your response here"
							value={answer}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setAnswer(e.currentTarget.value)
								e.currentTarget.value && setError('')
							}}
						/>
					)}
					<div
						css={`
							margin-top: ${_tempTheme.spacing2};
						`}
					/>
				</div>
			}
		/>
	) : review === 'OVERVIEW' ? (
		<ReviewFreeResponse
			index={questionIndex}
			studentAnswer={studentResponse?.answer}
			question={question}
		/>
	) : (
		<Fragment />
	)
}
/**
 * A component for a student to review the answer to their free response question
 * @param {{question: FreeResponseQuestion, studentAnswer?: StudentAnswer, index?: number,}} props
 * @returns {JSX.Element}
 */
function ReviewFreeResponse({
	question,
	studentAnswer,
	index,
}: {
	question: FreeResponse<ClientFields>
	studentAnswer?: StudentAnswer
	index?: number
}): JSX.Element {
	if (studentAnswer && !isFreeResponseAnswer(studentAnswer)) return <Fragment />

	const answer = studentAnswer?.value
	return (
		<StudentReviewWrapper index={index} prompt={question.phrase}>
			<Image media={question.media} />
			<p>{answer || <i>No answer</i>}</p>
		</StudentReviewWrapper>
	)
}

export const FRQuestionInput = styled.input`
	width: 75%;
	margin: 0 0 ${_tempTheme.spacing1};
	padding: ${_tempTheme.spacing2};
	font-size: inherit;
	border-radius: ${_tempTheme.spacing1};
`

export default StudentFreeResponse
